import React, { useState, useEffect, useContext, Fragment, ReactNode } from "react";
import moment, { Moment } from "moment";
import memoize from "memoize-one";
import { List, WindowScroller, ListRowRenderer, AutoSizer } from "react-virtualized";

import { itemsToBoardState, getEvents } from "../tools/events";
import { timesInStatuses, Stats, StatType, statsForStatuses, TimesInStatuses } from "../tools/stats";
import { getItemsMap } from "../tools";
import { Board, Labels } from "../types";
import ItemRow from "./ItemRow";
import Stat from "./Stat";
import AttentionBox from "../common/AttentionBox";
import { SettingsContext, Sorting } from "../SettingsProvider";
import { loadSet, saveSet } from "../tools/storage";

import "./StatsView.css";
import Tooltip, { Direction } from "../common/Tooltip";

interface Props {
    board: Board;
    labels: Labels;
    columnId: string;
    from: Moment;
    to: Moment;
    showStats: StatType[];
    globalNormalise?: boolean;
    itemSorting: Sorting;
}

const getMaxTimeSum = memoize((times: TimesInStatuses) => Math.max(...Object.values(times).map((t) => t["_sum"])));

const getSortedItems = (times: TimesInStatuses, sorting: Sorting): string[] => {
    const itemIds = [...Object.keys(times)];

    let sortingField: string;
    if ([Sorting.SELECTED_STATUSES_TIME_ASC, Sorting.SELECTED_STATUSES_TIME_DESC].includes(sorting)) {
        sortingField = "_sumSelected";
    } else {
        sortingField = "_sum";
    }

    let compare: (a: string, b: string) => number;
    if ([Sorting.SELECTED_STATUSES_TIME_ASC, Sorting.TOTAL_TIME_ASC].includes(sorting)) {
        compare = (a, b): number => times[a][sortingField] - times[b][sortingField];
    } else {
        compare = (a, b): number => times[b][sortingField] - times[a][sortingField];
    }

    itemIds.sort(compare);

    return itemIds;
};

const StatsView: React.FC<Props> = function ({
    board,
    labels,
    columnId,
    from,
    to,
    showStats,
    globalNormalise,
    itemSorting
}: Props) {
    const { context, settings } = useContext(SettingsContext);
    const storeKey = `selectedLabels-${context.instanceId}-${columnId}`;

    const [selectedLabelIds, setSelectedLabelIds] = useState<Set<string>>(new Set([]));
    const [times, setTimes] = useState<TimesInStatuses>();
    const [stats, setStats] = useState<Stats>();
    const [sortedItems, setSortedItems] = useState<string[]>([]);

    useEffect(() => {
        const f = async (): Promise<void> => {
            if (board && columnId) {
                const now = moment();
                const currentBoard = itemsToBoardState(board, now);
                const { events } = getEvents(currentBoard, board.activity_logs, columnId, "status");
                const times = timesInStatuses(events, columnId, from, to);
                setTimes(times);

                const rememberedLabelIds = (await loadSet(storeKey)) || new Set([]);
                // remove any labels which were saved, but are not in project anymore
                for (const labelId of Array.from(rememberedLabelIds)) {
                    if (labels.names[labelId] === undefined) {
                        rememberedLabelIds.delete(labelId);
                    }
                }
                setSelectedLabelIds(rememberedLabelIds);
            }
        };
        f();
    }, [board, columnId, from, to]); //eslint-disable-line

    useEffect(() => {
        if (times) {
            const toCalc: Set<StatType> = new Set(showStats);
            if (settings.statsCompareAgainst) {
                toCalc.add(settings.statsCompareAgainst);
            }
            if (!settings.statsShowUnselectedBars) {
                toCalc.add(StatType.MAX);
            }
            const newStats = statsForStatuses(times, selectedLabelIds, toCalc); // modifies times!
            setStats(newStats);
        }
    }, [selectedLabelIds, times, showStats, settings.statsCompareAgainst, settings.statsShowUnselectedBars]);

    useEffect(() => {
        if (times) {
            setSortedItems(getSortedItems(times, itemSorting));
        }
    }, [stats, itemSorting]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleSelectedLabelId = (labelId: string): void => {
        const newSet = new Set(selectedLabelIds);
        if (selectedLabelIds.has(labelId)) {
            newSet.delete(labelId);
        } else {
            newSet.add(labelId);
        }
        setSelectedLabelIds(newSet);
        saveSet(storeKey, newSet);
    };

    if (stats && times) {
        let maxTimeSum: number;
        if (globalNormalise) {
            maxTimeSum = getMaxTimeSum(times);
        }

        const itemsMap = getItemsMap(board);
        const compareAgainstMs = settings.statsCompareAgainst ? stats[settings.statsCompareAgainst] : undefined;

        const rowRenderer: ListRowRenderer = ({ index, key, style }) => {
            const itemId = sortedItems[index];
            const itemTimes: { [labelId: string]: number } | undefined = times[itemId];
            return (
                <ItemRow
                    key={key}
                    style={style}
                    item={itemsMap[itemId]}
                    times={itemTimes}
                    labels={labels}
                    selectedLabelIds={selectedLabelIds}
                    showUnselectedBars={settings.statsShowUnselectedBars}
                    compareAgainstMs={compareAgainstMs}
                    denominator={
                        settings.statsShowUnselectedBars || selectedLabelIds.size === 0
                            ? globalNormalise
                                ? maxTimeSum
                                : itemTimes
                                ? itemTimes["_sum"]
                                : 1
                            : stats[StatType.MAX]
                    }
                    index={index}
                    count={sortedItems.length}
                />
            );
        };

        const selectedLabelNames = labels.orderedIds
            .filter((labelId) => selectedLabelIds.has(labelId))
            .map((labelId) => labels.names[labelId]);

        return (
            <div className="stats_view">
                <div className="stats_view__labels">
                    {labels.orderedIds.map((labelId) => {
                        const style = labels.labelsColors[labelId];
                        const selected = selectedLabelIds.has(labelId);
                        return (
                            <div
                                key={labelId}
                                className={`labels__label ${selected ? "" : "unselected"}`}
                                onClick={(): void => toggleSelectedLabelId(labelId)}
                            >
                                <div
                                    className="labels__label__marker"
                                    style={{
                                        backgroundColor: style.color
                                    }}
                                />
                                <span className="labels__label__name">{labels.names[labelId]}</span>
                            </div>
                        );
                    })}
                </div>

                <div className="stats_view__stats">
                    {selectedLabelIds.size === 0 ? (
                        <AttentionBox title="Choose statuses">
                            <p>Choose one or more statuses above to see statistics for them.</p>
                            <p>
                                To measure <span className="strong">Lead Time</span> you might want to select all
                                statuses apart from last one in workflow (e.g. Done).
                            </p>
                            <p>
                                <span className="strong">Cycle Time</span> starts when the actual work begins - unselect
                                your Backlog status or equivalent.
                            </p>
                        </AttentionBox>
                    ) : showStats.length > 0 ? (
                        showStats.map((statType) => <Stat key={statType} type={statType} timeValue={stats[statType]} />)
                    ) : (
                        <AttentionBox
                            title="Selects statistics"
                            text="Select statistics you want to see in View Settings."
                        />
                    )}
                </div>

                <div className="stats_view__items">
                    {sortedItems.length > 0 ? (
                        <Fragment>
                            <div className="stats_view__items__header">
                                <div className="header_column name" />
                                <div className="header_column time tooltip">
                                    Selected
                                    <Tooltip
                                        type="dark"
                                        direction={Direction.TOP}
                                        text={
                                            selectedLabelIds.size > 0
                                                ? `Time in ${selectedLabelIds.size} selected status${
                                                      selectedLabelIds.size !== 1 ? "es" : ""
                                                  } (${selectedLabelNames.join(", ")})`
                                                : "Select statuses to see time spent in them"
                                        }
                                    />
                                </div>
                                <div className="header_column time tooltip">
                                    Total
                                    <Tooltip
                                        type="dark"
                                        direction={Direction.TOP}
                                        text={`Total time in all statuses`}
                                    />
                                </div>
                                <div className="header_column graph tooltip">
                                    Times in each status
                                    <Tooltip
                                        type="dark"
                                        direction={Direction.TOP}
                                        text={`Shows relative length of time spent in each status. Hover to see exact values.`}
                                    />
                                </div>
                            </div>

                            <AutoSizer disableHeight>
                                {({ width }): ReactNode => (
                                    <WindowScroller>
                                        {({ height, isScrolling, onChildScroll, scrollTop }): ReactNode => (
                                            <List
                                                autoHeight
                                                height={height}
                                                width={width}
                                                rowHeight={36}
                                                rowCount={sortedItems.length}
                                                rowRenderer={rowRenderer}
                                                isScrolling={isScrolling}
                                                onScroll={onChildScroll}
                                                scrollTop={scrollTop}
                                            />
                                        )}
                                    </WindowScroller>
                                )}
                            </AutoSizer>
                        </Fragment>
                    ) : (
                        <div className="stats_view__no_items">
                            <AttentionBox title="No tasks to show">
                                <p>Did you choose correct Status Column, From Date and To Date in View Settings?</p>
                                <p>
                                    <span className="strong">Note:</span> Tasks/items for which selected Status Column
                                    wasn&apos;t set within From - To Dates won&apos;t show here.
                                </p>
                            </AttentionBox>
                        </div>
                    )}
                </div>
            </div>
        );
    } else {
        return null;
    }
};

export default StatsView;
